import React, {
  useState,
  useEffect,
  useContext,
} from "react";

import {
  auth,
  db,
} from "../firebase/firebaseConfig";

import {
  collection,
  getDocs,
  where,
  query,
  limit,
} from "firebase/firestore";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import Alertas from "./Alertas";
import ActualizarTipoDb from "../firebase/ActualizarTipoDb";
import useObtenerTipo from "../hooks/useObtenerTipo";

import { UserContext } from "../context/UserContext";

import {
  Container,
  Segment,
  Header,
  Form,
  Icon,
  Button,
  Message,
} from "semantic-ui-react";

const ActualizaTipo = () => {
  const user = auth.currentUser;
  const { users } = useContext(UserContext);

  const navigate = useNavigate();
  const { id } = useParams();

  const [tipo] = useObtenerTipo(id);

  // ==========================================================
  // ESTADOS
  // ==========================================================

  const [
    nuevoCampo,
    setNuevoCampo,
  ] = useState("");

  /*
   * Muy importante:
   * guardamos el nombre ORIGINAL del Tipo.
   *
   * La verificación de propagación siempre debe hacerse
   * usando este nombre y NO lo que el usuario esté escribiendo.
   */
  const [
    tipoOriginal,
    setTipoOriginal,
  ] = useState("");

  const [
    alerta,
    cambiarAlerta,
  ] = useState({});

  const [
    estadoAlerta,
    cambiarEstadoAlerta,
  ] = useState(false);

  const [
    isSaving,
    setIsSaving,
  ] = useState(false);

  const [
    verificandoUso,
    setVerificandoUso,
  ] = useState(false);

  const [
    tipoBloqueado,
    setTipoBloqueado,
  ] = useState(false);

  // ==========================================================
  // VOLVER
  // ==========================================================

  const volver = () => {
    navigate(
      "/misequipos/agregartipo"
    );
  };

  // ==========================================================
  // VERIFICAR SI EL TIPO YA ESTÁ SIENDO UTILIZADO
  // ==========================================================

  const verificarTipoEnUso =
    async (nombreTipo) => {
      if (!users?.emp_id) {
        throw new Error(
          "No se encontró la empresa del usuario"
        );
      }

      const tipoNormalizado =
        String(
          nombreTipo || ""
        )
          .toLocaleUpperCase()
          .trim();

      if (!tipoNormalizado) {
        return false;
      }

      // ======================================================
      // 1. EQUIPOS
      // ======================================================

      const qEquipos =
        query(
          collection(
            db,
            "equipos"
          ),

          where(
            "emp_id",
            "==",
            users.emp_id
          ),

          where(
            "tipo",
            "==",
            tipoNormalizado
          ),

          limit(1)
        );

      const snapEquipos =
        await getDocs(
          qEquipos
        );

      /*
       * Si aparece en equipos,
       * ya fue propagado.
       */
      if (
        !snapEquipos.empty
      ) {
        return true;
      }

      // ======================================================
      // 2. INGRESOS SERVICIO TÉCNICO
      // ======================================================

      const qIngresos =
        query(
          collection(
            db,
            "ingresostcab"
          ),

          where(
            "emp_id",
            "==",
            users.emp_id
          ),

          where(
            "tipo",
            "==",
            tipoNormalizado
          ),

          limit(1)
        );

      const snapIngresos =
        await getDocs(
          qIngresos
        );

      if (
        !snapIngresos.empty
      ) {
        return true;
      }

      /*
       * No se encontró ni en equipos
       * ni en ingresostcab.
       *
       * Puede modificarse.
       */
      return false;
    };

  // ==========================================================
  // VERIFICAR SI YA EXISTE OTRO TIPO CON EL NUEVO NOMBRE
  // ==========================================================

  const verificarNombreDuplicado =
    async (nombreTipo) => {
      if (!users?.emp_id) {
        return false;
      }

      const tipoNormalizado =
        String(
          nombreTipo || ""
        )
          .toLocaleUpperCase()
          .trim();

      if (!tipoNormalizado) {
        return false;
      }

      const qTipos =
        query(
          collection(
            db,
            "tipos"
          ),

          where(
            "emp_id",
            "==",
            users.emp_id
          ),

          where(
            "tipo",
            "==",
            tipoNormalizado
          )
        );

      const snapTipos =
        await getDocs(
          qTipos
        );

      /*
       * Si encuentra el mismo documento que estamos editando,
       * NO se considera duplicado.
       *
       * Solo devuelve true si existe OTRO documento.
       */
      return snapTipos.docs.some(
        (doc) =>
          doc.id !== id
      );
    };

  // ==========================================================
  // GUARDAR CAMBIOS
  // ==========================================================

  const handleSubmit =
    async (e) => {
      e.preventDefault();

      cambiarEstadoAlerta(
        false
      );

      cambiarAlerta({});

      const nombreNuevo =
        String(
          nuevoCampo || ""
        )
          .toLocaleUpperCase()
          .trim();

      const originalNormalizado =
        String(
          tipoOriginal || ""
        )
          .toLocaleUpperCase()
          .trim();

      // ======================================================
      // VALIDAR VACÍO
      // ======================================================

      if (!nombreNuevo) {
        cambiarEstadoAlerta(
          true
        );

        cambiarAlerta({
          tipo: "error",
          mensaje:
            "El Tipo de Equipamiento no puede estar vacío.",
        });

        return;
      }

      // ======================================================
      // VALIDAR QUE HAYA CAMBIOS
      // ======================================================

      if (
        nombreNuevo ===
        originalNormalizado
      ) {
        cambiarEstadoAlerta(
          true
        );

        cambiarAlerta({
          tipo: "error",
          mensaje:
            "No se han realizado cambios en el Tipo de Equipamiento.",
        });

        return;
      }

      setIsSaving(true);

      try {
        // ====================================================
        // REVALIDAR PROPAGACIÓN
        //
        // MUY IMPORTANTE:
        // consultar usando tipoOriginal.
        // ====================================================

        const enUso =
          await verificarTipoEnUso(
            tipoOriginal
          );

        if (enUso) {
          setTipoBloqueado(
            true
          );

          cambiarEstadoAlerta(
            true
          );

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "Este tipo ya está siendo utilizado en el sistema y no puede modificarse.",
          });

          return;
        }

        // ====================================================
        // VALIDAR DUPLICADO
        // ====================================================

        const duplicado =
          await verificarNombreDuplicado(
            nombreNuevo
          );

        if (duplicado) {
          cambiarEstadoAlerta(
            true
          );

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "Ya existe este Tipo de Equipamiento.",
          });

          return;
        }

        // ====================================================
        // ACTUALIZAR
        // ====================================================

        await ActualizarTipoDb({
          id:
            id,

          tipo:
            nombreNuevo,

          userMod:
            user?.email || "",

          fechaMod:
            new Date(),
        });

        /*
         * Luego de actualizar, el nuevo nombre
         * pasa a ser el original.
         */
        setTipoOriginal(
          nombreNuevo
        );

        setNuevoCampo(
          nombreNuevo
        );

        cambiarEstadoAlerta(
          true
        );

        cambiarAlerta({
          tipo: "exito",
          mensaje:
            "Tipo de Equipamiento actualizado correctamente.",
        });
      } catch (error) {
        console.error(
          "Error actualizando Tipo:",
          error
        );

        cambiarEstadoAlerta(
          true
        );

        cambiarAlerta({
          tipo: "error",
          mensaje:
            "No fue posible actualizar el Tipo de Equipamiento.",
        });
      } finally {
        setIsSaving(false);
      }
    };

  // ==========================================================
  // CARGAR TIPO Y VERIFICAR SI ESTÁ EN USO
  // ==========================================================

  useEffect(() => {
    if (
      !tipo?.tipo ||
      !users?.emp_id
    ) {
      return;
    }

    const nombreTipo =
      String(
        tipo.tipo || ""
      )
        .toLocaleUpperCase()
        .trim();

    /*
     * Cargamos tanto el input como
     * el valor original.
     */
    setNuevoCampo(
      nombreTipo
    );

    setTipoOriginal(
      nombreTipo
    );

    // ========================================================
    // COMPROBAR PROPAGACIÓN
    // ========================================================

    const comprobarUso =
      async () => {
        setVerificandoUso(
          true
        );

        try {
          const enUso =
            await verificarTipoEnUso(
              nombreTipo
            );

          setTipoBloqueado(
            enUso
          );
        } catch (error) {
          console.error(
            "Error verificando uso del Tipo:",
            error
          );

          /*
           * Si Firestore falla y no podemos saber
           * si está propagado, bloqueamos por seguridad.
           */
          setTipoBloqueado(
            true
          );

          cambiarEstadoAlerta(
            true
          );

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "No fue posible verificar si el Tipo puede modificarse.",
          });
        } finally {
          setVerificandoUso(
            false
          );
        }
      };

    comprobarUso();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tipo,
    users?.emp_id,
  ]);

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <Container
      fluid
      style={{
        maxWidth:
          1400,
        padding:
          "24px 28px",
        background:
          "#f8fafc",
        minHeight:
          "100vh",
      }}
    >
      {/* ==================================================== */}
      {/* ENCABEZADO */}
      {/* ==================================================== */}

      <div
        style={{
          display:
            "flex",
          alignItems:
            "center",
          gap:
            14,
          marginBottom:
            20,
        }}
      >
        <div
          style={{
            width:
              48,
            height:
              48,
            minWidth:
              48,
            borderRadius:
              12,
            background:
              "#e5f7f7",
            display:
              "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
          }}
        >
          <Icon
            name="edit outline"
            style={{
              margin:
                0,
              color:
                "#0d9da5",
              fontSize:
                21,
            }}
          />
        </div>

        <div>
          <Header
            as="h2"
            style={{
              margin:
                0,
              color:
                "#172b4d",
              fontSize:
                24,
              fontWeight:
                700,
            }}
          >
            Actualizar Tipo
          </Header>

          <div
            style={{
              color:
                "#8793a5",
              fontSize:
                13,
              marginTop:
                4,
            }}
          >
            Modifica el Tipo de Equipamiento Médico
          </div>
        </div>
      </div>

      {/* ==================================================== */}
      {/* TARJETA */}
      {/* ==================================================== */}

      <Segment
        style={{
          borderRadius:
            8,
          border:
            "1px solid #e5e9ef",
          boxShadow:
            "0 2px 6px rgba(0,0,0,0.04)",
          padding:
            0,
          background:
            "#ffffff",
          overflow:
            "hidden",
        }}
      >
        {/* ================================================== */}
        {/* CABECERA TARJETA */}
        {/* ================================================== */}

        <div
          style={{
            padding:
              "14px 18px",
            borderBottom:
              "1px solid #edf0f4",
            display:
              "flex",
            alignItems:
              "center",
            gap:
              9,
            color:
              "#253858",
            fontSize:
              14,
            fontWeight:
              600,
          }}
        >
          <Icon
            name="tag"
            style={{
              color:
                "#0d9da5",
            }}
          />

          Datos del Tipo de Equipamiento
        </div>

        <div
          style={{
            padding:
              22,
          }}
        >
          {/* ================================================ */}
          {/* VERIFICANDO */}
          {/* ================================================ */}

          {verificandoUso && (
            <Message
              icon
              size="small"
              style={{
                marginTop:
                  0,
              }}
            >
              <Icon
                name="circle notched"
                loading
              />

              <Message.Content>
                <Message.Header>
                  Verificando Tipo
                </Message.Header>

                Estamos comprobando si el Tipo puede ser modificado.
              </Message.Content>
            </Message>
          )}

          {/* ================================================ */}
          {/* BLOQUEADO */}
          {/* ================================================ */}

          {!verificandoUso &&
            tipoBloqueado && (
              <Message
                warning
                icon
                style={{
                  marginTop:
                    0,
                }}
              >
                <Icon
                  name="lock"
                />

                <Message.Content>
                  <Message.Header>
                    Tipo protegido
                  </Message.Header>

                  Este tipo ya está siendo utilizado en el sistema y no puede modificarse.
                </Message.Content>
              </Message>
            )}

          {/* ================================================ */}
          {/* DISPONIBLE */}
          {/* ================================================ */}

          {!verificandoUso &&
            !tipoBloqueado &&
            tipoOriginal && (
              <Message
                positive
                icon
                size="small"
                style={{
                  marginTop:
                    0,
                }}
              >
                <Icon
                  name="unlock"
                />

                <Message.Content>
                  <Message.Header>
                    Tipo disponible para edición
                  </Message.Header>

                  Este tipo todavía no ha sido utilizado en el sistema.
                </Message.Content>
              </Message>
            )}

          {/* ================================================ */}
          {/* FORMULARIO */}
          {/* ================================================ */}

          <Form
            size="large"
            onSubmit={
              handleSubmit
            }
          >
            <Form.Field>
              <label
                style={{
                  color:
                    "#344563",
                  fontSize:
                    13,
                  fontWeight:
                    600,
                  marginBottom:
                    7,
                  display:
                    "block",
                }}
              >
                Tipo de Equipamiento
              </label>

              <Form.Input
                fluid
                type="text"
                name="tipo"
                icon="tag"
                iconPosition="left"
                placeholder="Ingrese Tipo de Equipamiento"
                value={
                  nuevoCampo
                }
                disabled={
                  isSaving ||
                  verificandoUso ||
                  tipoBloqueado
                }
                onChange={(
                  e
                ) =>
                  setNuevoCampo(
                    e.target.value
                  )
                }
              />
            </Form.Field>

            {/* ============================================== */}
            {/* BOTONES */}
            {/* ============================================== */}

            <div
              style={{
                display:
                  "flex",
                justifyContent:
                  "flex-end",
                alignItems:
                  "center",
                gap:
                  10,
                marginTop:
                  30,
                paddingTop:
                  18,
                borderTop:
                  "1px solid #edf0f4",
                flexWrap:
                  "wrap",
              }}
            >
              <Button
                type="button"
                onClick={
                  volver
                }
                disabled={
                  isSaving
                }
                style={{
                  minWidth:
                    110,
                  background:
                    "#ffffff",
                  color:
                    "#526273",
                  border:
                    "1px solid #d7dee7",
                  borderRadius:
                    6,
                  boxShadow:
                    "none",
                }}
              >
                <Icon
                  name="arrow left"
                />

                Volver
              </Button>

              {/* ============================================ */}
              {/* SOLO MOSTRAMOS ACTUALIZAR SI PUEDE EDITARSE */}
              {/* ============================================ */}

              {!tipoBloqueado && (
                <Button
                  type="submit"
                  loading={
                    isSaving
                  }
                  disabled={
                    isSaving ||
                    verificandoUso ||
                    !tipoOriginal
                  }
                  style={{
                    minWidth:
                      125,
                    background:
                      "#0d9da5",
                    color:
                      "#ffffff",
                    border:
                      "1px solid #0d9da5",
                    borderRadius:
                      6,
                    boxShadow:
                      "none",
                  }}
                >
                  {!isSaving && (
                    <Icon
                      name="save"
                    />
                  )}

                  Actualizar
                </Button>
              )}
            </div>
          </Form>
        </div>
      </Segment>

      {/* ==================================================== */}
      {/* ALERTAS */}
      {/* ==================================================== */}

      <Alertas
        tipo={
          alerta.tipo
        }
        mensaje={
          alerta.mensaje
        }
        estadoAlerta={
          estadoAlerta
        }
        cambiarEstadoAlerta={
          cambiarEstadoAlerta
        }
      />
    </Container>
  );
};

export default ActualizaTipo;